import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { clientAuthApi, expertAuthApi, getErrorMessage } from '../../lib/http';
import logo from '../../assets/logo.png';

// Validation schema
const resetSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  password_confirmation: z.string(),
}).refine((data) => data.password === data.password_confirmation, {
  message: "Passwords don't match",
  path: ['password_confirmation'],
});

type ResetFormData = z.infer<typeof resetSchema>;

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>();

  const onSubmit = async (data: ResetFormData) => {
    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      // Try client API first, then expert API
      try {
        await clientAuthApi.reset({
          token,
          password: data.password,
          password_confirmation: data.password_confirmation,
        });
      } catch {
        // If client fails, try expert
        await expertAuthApi.reset({
          token,
          password: data.password,
          password_confirmation: data.password_confirmation,
        });
      }

      setSuccess(true);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex flex-col p-4">
        <div className="w-full max-w-md mx-auto flex flex-col flex-1">
          <div className="flex justify-center pt-4">
            <img src={logo} alt="Book A Day" className="h-12 w-auto" />
          </div>

          <div className="flex-1" />

          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Invalid Reset Link
            </h2>

            <p className="text-gray-600 mb-6">
              This password reset link is invalid or has expired.
              Please request a new one.
            </p>

            <Link
              to="/auth/select"
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              Back to Home
            </Link>
          </div>

          <div className="flex-1" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex flex-col p-4">
      <div className="w-full max-w-md mx-auto flex flex-col flex-1">
        <div className="flex justify-center pt-4">
          <img src={logo} alt="Book A Day" className="h-12 w-auto" />
        </div>

        <div className="flex-1" />

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Password Reset Successfully
              </h2>

              <p className="text-gray-600 mb-6">
                Your password has been updated. You can now log in with your new password.
              </p>

              <Link
                to="/auth/select"
                className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Go to Login
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Reset Your Password
              </h2>
              <p className="text-gray-600 mb-6">
                Enter your new password below.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {/* Error Alert */}
                {error && (
                  <div
                    role="alert"
                    className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm"
                    aria-live="polite"
                  >
                    {error}
                  </div>
                )}

                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    New Password
                  </label>
                  <input
                    {...register('password')}
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                      errors.password ? 'border-red-300' : 'border-gray-300'
                    }`}
                    aria-invalid={errors.password ? 'true' : 'false'}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                  />
                  {errors.password && (
                    <p id="password-error" className="mt-1 text-sm text-red-600">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label
                    htmlFor="password_confirmation"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Confirm New Password
                  </label>
                  <input
                    {...register('password_confirmation')}
                    id="password_confirmation"
                    type="password"
                    autoComplete="new-password"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                      errors.password_confirmation ? 'border-red-300' : 'border-gray-300'
                    }`}
                    aria-invalid={errors.password_confirmation ? 'true' : 'false'}
                    aria-describedby={errors.password_confirmation ? 'password-confirmation-error' : undefined}
                  />
                  {errors.password_confirmation && (
                    <p id="password-confirmation-error" className="mt-1 text-sm text-red-600">
                      {errors.password_confirmation.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-purple-200"
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>

                {/* Links */}
                <div className="text-center text-sm">
                  <Link
                    to="/auth/select"
                    className="text-purple-600 hover:underline"
                  >
                    Back to login
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>

        <div className="flex-1" />

        <div className="text-center pb-4 text-sm text-gray-500">
          <p>
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-purple-600 hover:underline">
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="/privacy" className="text-purple-600 hover:underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
